import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, Cell } from "recharts";
import { Package, Users, Download, Layers } from "lucide-react";
import BackButton from "@/components/BackButton";

const STRAIN_HUES = ["#FBC90C", "#E15C2C", "#B02065", "#A0A0A0", "#5A5A5A"];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground animate-pulse">Loading analytics…</div>;

  return (
    <div className="space-y-10 slide-up" data-testid="analytics-page">
      <BackButton />
      <header>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Signals</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-2">How Apollo assets move through the ecosystem.</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Layers} label="Total Assets" value={data.totals.assets} />
        <Stat icon={Package} label="Products" value={data.totals.products} />
        <Stat icon={Users} label="Users" value={data.totals.users} />
        <Stat icon={Download} label="Downloads" value={data.totals.downloads} />
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Downloads · Last 14 Days</div>
          {data.timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.timeline}>
                <XAxis dataKey="date" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={10} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2A2A2A", borderRadius: 4, color: "#fff", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#FBC90C" strokeWidth={2} dot={{ fill: "#FBC90C", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
        <div className="border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Downloads by State</div>
          {data.downloads_by_state.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.downloads_by_state}>
                <XAxis dataKey="state" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={10} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2A2A2A", borderRadius: 4, color: "#fff", fontSize: 12 }} />
                <Bar dataKey="count">
                  {data.downloads_by_state.map((_, i) => <Cell key={i} fill={STRAIN_HUES[i % STRAIN_HUES.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-4">
        <div className="border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Top Downloaded Assets</div>
          <div className="space-y-3">
            {data.top_assets.length === 0 && <EmptyChart />}
            {data.top_assets.map((t, i) => (
              <div key={t.asset_id || i} className="flex items-center gap-4">
                <div className="font-display text-2xl font-bold w-8">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="w-full h-1 bg-surface-elev mt-1.5 relative rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-0" style={{ width: `${Math.min(100, (t.downloads / (data.top_assets[0]?.downloads || 1)) * 100)}%`, background: STRAIN_HUES[i % STRAIN_HUES.length] }} />
                  </div>
                </div>
                <div className="font-mono text-xs text-muted-foreground w-8 text-right">{t.downloads}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Recent Activity</div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto">
            {data.recent_activity.length === 0 && <EmptyChart />}
            {data.recent_activity.map((e) => (
              <div key={e.id} className="flex items-center gap-3 text-xs py-1.5 border-b border-border last:border-b-0">
                <span className={`px-1.5 py-0.5 font-mono uppercase tracking-widest text-[9px] rounded-sm ${e.type === "download" ? "strain-indica" : "bg-surface-elev"}`}>{e.type}</span>
                <span className="flex-1 truncate">{e.asset_title || "asset"}</span>
                <span className="text-muted-foreground">{e.user_name || "—"}</span>
                <span className="text-muted-foreground font-mono">{new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-sm p-4 bg-surface">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</span>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-40 flex items-center justify-center text-xs text-muted-foreground border border-dashed border-border rounded-sm">
      No data yet — download some assets to populate.
    </div>
  );
}
